import type { ReactNode } from "react";
import Collapse from "@mui/material/Collapse";
import { useFoldTimeout } from "@/lib/useFoldTimeout";

// The one page header (BRK-S262; Nav, 2026-09-28: "There's too much margin
// between the form header and navbar"). Every portal page opens with it, the
// base views included, so the top bar sits the same distance from every
// title: the shell pads <main> (16px on a phone, 24px from sm), and the header
// adds nothing above itself and 16px below.
//
// The title is the page's one h1, in the scale's title style
// (docs/design-system.md); test/lib/page-header-conformance.test.ts keeps
// pages from writing their own. It carries no navigation: the top bar's
// breadcrumb is the one trail and the way back (BRK-S269; Nav, 2026-09-28: "we
// don't need to use the page header, we need to use the navbar and make it
// standard for the navigation and breadcrumbs"). Every other part is optional:
// - `above`, over the title (a submission flow's progress);
// - `leading`, before it (a record's avatar);
// - `status`, beside it, outside the h1 so the heading's name is the title
//   alone;
// - `meta`, the dim line under it;
// - `actions`, on the right, wrapping under the title when there's no room.
//
// `folded` (BRK-S266, BRK-S267) slides `above`, `leading`, `status` and
// `meta` away, and `trail` (a record's id) in
// before the title. The title and the actions stay. On a phone the trail
// wraps above the title rather than squeeze it.
//
// `titleHidden` keeps the title as the page's h1 for screen readers but out of
// sight, for a page the navbar's breadcrumb already names: every list (Nav,
// 2026-09-28: "We can remove the title of accounts from listview as we have
// already moved it to the navbar"; BRK-S269).
export function PageHeader({
  title,
  titleHidden = false,
  meta,
  actions,
  above,
  leading,
  status,
  folded = false,
  trail,
}: {
  title: ReactNode;
  titleHidden?: boolean;
  meta?: ReactNode;
  actions?: ReactNode;
  above?: ReactNode;
  leading?: ReactNode;
  status?: ReactNode;
  folded?: boolean;
  trail?: ReactNode;
}) {
  const timeout = useFoldTimeout();
  const fold = { timeout, unmountOnExit: true } as const;
  return (
    <header data-page-header className="mb-4 flex flex-col">
      {above && (
        <Collapse in={!folded} {...fold}>
          <div className="pb-2">{above}</div>
        </Collapse>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-y-1">
          {leading && (
            <Collapse orientation="horizontal" in={!folded} {...fold}>
              <div className="pr-3">{leading}</div>
            </Collapse>
          )}
          {trail && (
            <Collapse orientation="horizontal" in={folded} {...fold}>
              <div className="pr-3">{trail}</div>
            </Collapse>
          )}
          <div className="flex min-w-0 flex-[1_1_12rem] flex-col">
            <div className="flex flex-wrap items-center gap-y-1">
              <h1
                className={titleHidden ? "sr-only" : "text-title text-brk-text"}
              >
                {title}
              </h1>
              {status && (
                <Collapse orientation="horizontal" in={!folded} {...fold}>
                  <div className="pl-2">{status}</div>
                </Collapse>
              )}
            </div>
            {meta && (
              <Collapse in={!folded} {...fold}>
                <div className="pt-0.5 text-meta text-brk-dim">{meta}</div>
              </Collapse>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  );
}
